import type { AuthType } from '@contexts/auth-provider/AuthProvider.types';

import { PublicClientApplication } from '@azure/msal-browser';
import { msalConfigB2C, msalConfigB2E } from '@configs/authConfig';

import { detectAuthTypeFromStorage } from './msalInstance.utils';

/**
 * Shared MSAL instance for both React (MsalProvider) and non-React contexts (axios interceptors)
 *
 * This module provides a singleton MSAL instance that is shared between:
 * - MsalProvider (React context)
 * - Axios interceptors (acquireAccessToken)
 *
 * The instance type (B2C/B2E) is determined by:
 * 1. Existing accounts in localStorage (detected at init)
 * 2. `pending-auth-type` localStorage flag (set before redirect auth)
 *
 * IMPORTANT: MsalProvider calls `setMsalInstance()` to register its instance,
 * ensuring both React and axios use the same instance.
 *
 * @example
 * // In axios interceptor
 * const msal = await getMsalInstance();
 * const account = msal.getActiveAccount();
 * const token = await msal.acquireTokenSilent({ account, scopes });
 */

let msalInstanceSingleton: PublicClientApplication | null = null;
let currentAuthType: AuthType = 'b2c';

/**
 * Set the shared MSAL instance (called by MsalProvider)
 * This ensures axios interceptors use the same instance as React components
 */
export function setMsalInstance(
  instance: PublicClientApplication,
  authType: AuthType
): void {
  msalInstanceSingleton = instance;
  currentAuthType = authType;
}

/**
 * Get the current auth type
 */
export function getCurrentAuthType(): AuthType {
  return currentAuthType;
}

/**
 * Get the shared MSAL instance
 * If not set by MsalProvider, creates one based on localStorage detection
 */
export async function getMsalInstance(): Promise<PublicClientApplication> {
  if (!msalInstanceSingleton) {
    // Detect auth type from localStorage
    const detectedAuthType = detectAuthTypeFromStorage();
    currentAuthType = detectedAuthType;

    const config = detectedAuthType === 'b2e' ? msalConfigB2E : msalConfigB2C;
    msalInstanceSingleton = new PublicClientApplication(config);
    await msalInstanceSingleton.initialize();
  }
  return msalInstanceSingleton;
}

/**
 * Clear the singleton (used during logout)
 */
export function clearMsalInstanceSingleton(): void {
  msalInstanceSingleton = null;
  currentAuthType = 'b2c';
}
